import { z } from '@hono/zod-openapi'
import {
  CONTACT_CATEGORY_CONTRACTOR,
  CONTACT_CATEGORY_EMERGENCY,
  CONTACT_CATEGORY_OC,
  CONTACT_CATEGORY_OTHER,
  CONTACT_CATEGORY_OWNER,
  CONTACT_CATEGORY_RESIDENT,
  CONTACT_CATEGORY_TENANT,
  CONTACT_TYPE_CHAT,
  CONTACT_TYPE_EMAIL,
  CONTACT_TYPE_FAX,
  CONTACT_TYPE_OTHER,
  CONTACT_TYPE_PHONE,
  CONTACT_TYPE_SMS,
  TICKET_STATUS_COMPLETED,
  TICKET_STATUS_CONTRACTOR_IN_PROGRESS,
  TICKET_STATUS_OC_IN_PROGRESS,
  TICKET_STATUS_OWNER_IN_PROGRESS,
} from './ticket_type'

// id
export const idZ = z.number().openapi({ description: '対応履歴ID', example: 1 })
export type id = z.infer<typeof idZ>

// ticketId
export const ticketIdZ = z
  .number()
  .openapi({ description: '紐づくチケットID', example: 42 })
export type ticketId = z.infer<typeof ticketIdZ>

// status
export const statusZ = z
  .enum([
    TICKET_STATUS_OC_IN_PROGRESS,
    TICKET_STATUS_OWNER_IN_PROGRESS,
    TICKET_STATUS_CONTRACTOR_IN_PROGRESS,
    TICKET_STATUS_COMPLETED,
  ])
  .openapi({
    description:
      'この対応履歴を登録した時点のチケットステータス（oc_in_progress:OC対応中 / owner_in_progress:オーナー対応中 / contractor_in_progress:業者対応中 / completed:完了）',
    example: TICKET_STATUS_OC_IN_PROGRESS,
  })
export type status = z.infer<typeof statusZ>

// contactAt
export const contactAtStringZ = z
  .string()
  .openapi({ example: '2024-05-20T09:00:00.000Z', description: '連絡日時' })
export type contactAtString = z.infer<typeof contactAtStringZ>

export const contactAtDateZ = z
  .date()
  .openapi({ example: '2024-05-20T09:00:00.000Z', description: '連絡日時' })
export type contactAtDate = z.infer<typeof contactAtDateZ>

// contactType
export const contactTypeZ = z
  .enum([
    CONTACT_TYPE_PHONE,
    CONTACT_TYPE_EMAIL,
    CONTACT_TYPE_SMS,
    CONTACT_TYPE_CHAT,
    CONTACT_TYPE_FAX,
    CONTACT_TYPE_OTHER,
  ])
  .openapi({
    description:
      '連絡手段（phone:電話 / email:メール / sms:SMS / chat:チャット / fax:FAX / other:その他）',
    example: CONTACT_TYPE_PHONE,
  })
export type contactType = z.infer<typeof contactTypeZ>

// contactFromCategory
export const contactFromCategoryZ = z
  .enum([
    CONTACT_CATEGORY_OWNER,
    CONTACT_CATEGORY_TENANT,
    CONTACT_CATEGORY_RESIDENT,
    CONTACT_CATEGORY_OC,
    CONTACT_CATEGORY_CONTRACTOR,
    CONTACT_CATEGORY_EMERGENCY,
    CONTACT_CATEGORY_OTHER,
  ])
  .openapi({
    description:
      '連絡元の区分（owner:オーナー / tenant:賃借人 / resident:入居者(賃借人以外) / oc:OC / contractor:業者 / emergency:緊急連絡センター / other:その他）',
    example: CONTACT_CATEGORY_OWNER,
  })
export type contactFromCategory = z.infer<typeof contactFromCategoryZ>

// contactFromOwnerId
export const contactFromOwnerIdZ = z
  .string()
  .nullable()
  .openapi({ example: 'owner-uuid', description: '連絡者(FROM)オーナーID' })
export type contactFromOwnerId = z.infer<typeof contactFromOwnerIdZ>

// contactFromTenantId
export const contactFromTenantIdZ = z
  .number()
  .nullable()
  .openapi({ example: 123, description: '連絡者(FROM)賃借人ID' })
export type contactFromTenantId = z.infer<typeof contactFromTenantIdZ>

// contactFromAdminId
export const contactFromAdminIdZ = z.string().nullable().openapi({
  example: 'a4f2f2e6-2d5a-4c5f-a2dd-1234567890ab',
  description: '連絡者(FROM)管理者ID',
})
export type contactFromAdminId = z.infer<typeof contactFromAdminIdZ>

// contactFromText
export const contactFromTextZ = z
  .string()
  .nullable()
  .openapi({ example: 'テキスト', description: '連絡者(FROM)テキスト' })
export type contactFromText = z.infer<typeof contactFromTextZ>

// contactToCategory
export const contactToCategoryZ = z
  .enum([
    CONTACT_CATEGORY_OWNER,
    CONTACT_CATEGORY_TENANT,
    CONTACT_CATEGORY_RESIDENT,
    CONTACT_CATEGORY_OC,
    CONTACT_CATEGORY_CONTRACTOR,
    CONTACT_CATEGORY_EMERGENCY,
    CONTACT_CATEGORY_OTHER,
  ])
  .openapi({
    description:
      '連絡先の区分（owner:オーナー / tenant:賃借人 / resident:入居者(賃借人以外) / oc:OC / contractor:業者 / emergency:緊急連絡センター / other:その他）',
    example: CONTACT_CATEGORY_TENANT,
  })
export type contactToCategory = z.infer<typeof contactToCategoryZ>

// contactToOwnerId
export const contactToOwnerIdZ = z
  .string()
  .nullable()
  .openapi({ example: 'owner-uuid', description: '連絡者(TO)オーナーID' })
export type contactToOwnerId = z.infer<typeof contactToOwnerIdZ>

// contactToTenantId
export const contactToTenantIdZ = z
  .number()
  .nullable()
  .openapi({ example: 123, description: '連絡者(TO)賃借人ID' })
export type contactToTenantId = z.infer<typeof contactToTenantIdZ>

// contactToAdminId
export const contactToAdminIdZ = z.string().nullable().openapi({
  example: 'a4f2f2e6-2d5a-4c5f-a2dd-1234567890ab',
  description: '連絡者(TO)管理者ID',
})
export type contactToAdminId = z.infer<typeof contactToAdminIdZ>

// contactToText
export const contactToTextZ = z
  .string()
  .nullable()
  .openapi({ example: 'テキスト', description: '連絡者(TO)テキスト' })
export type contactToText = z.infer<typeof contactToTextZ>

// content
export const contentZ = z
  .string()
  .openapi({ example: '配管の点検を依頼しました。', description: '連絡内容' })
export type content = z.infer<typeof contentZ>

// createdAt
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-05-20T09:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-05-20T09:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>
